import { ApolloQueryResult } from '@apollo/client';
import * as queryTypes from './graphqlTypes';
import rootGetters from '../getters';
import rootSetters from '../actions';
import {
  defaultBlurCompanyForm,
  defaultBlurDepositForm,
  defaultBlurInvoiceForm,
  defaultBlurLogisticForm,
  defaultBlurLogisticOriginForm,
  defaultBlurManyDeposits,
  defaultBlurTaxForm,
} from './utils';

export const createCompany =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Company>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const newCompanyForm = getInvoice('forms.companies.create');
      const companyCondition = getRuleCondition('create', 'Company');
      const clientId = companyCondition?.clientId?.in?.[0] ?? 0;
      MUTATE<CreateCompanyMutation>({
        mutation: queryTypes.CREATE_COMPANY,
        variables: {
          clientId,
          createCompanyInput: {
            ...newCompanyForm,
          },
        },
      })
        .then((result) => {
          const company = result.data?.createCompany!;
          resolve(company);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const createTax = () => (dispatch: DispatchType, getState: () => any) =>
  new Promise<Tax>((resolve, reject) => {
    const {
      invoice: { getInvoice },
      user: { getRuleCondition },
    } = rootGetters(getState());
    const {
      graphql: { MUTATE },
    } = rootSetters(dispatch);
    const newTaxForm = getInvoice('forms.taxes.create');
    const taxCondition = getRuleCondition('create', 'Tax');
    const clientId = taxCondition?.clientId?.in?.[0] ?? 0;

    MUTATE<CreateTaxMutation>({
      mutation: queryTypes.CREATE_TAX,
      variables: {
        clientId,
        createTaxInput: {
          ...newTaxForm,
        },
      },
    })
      .then((result) => {
        const tax = result.data?.createTax!;
        resolve(tax);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const createInvoice =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Invoice>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getCurrentUser, getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const user = getCurrentUser();
      const invoiceCondition = getRuleCondition('create', 'Invoice');
      const clientId = invoiceCondition?.clientId?.in?.[0] ?? 0;
      const selectedGroupId = getInvoice("selectedGroupId");
      const {
        company: { id: companyId },
        invoiceTaxes,
        group,
        ...createInvoiceInput
      } = getInvoice('forms.invoices.create');
      const createInvoiceTaxesInput = invoiceTaxes.map(
        ({ value, tax: { id } }) => ({ taxId: id, value }),
      );
      MUTATE<CreateInvoiceMutation>({
        mutation: queryTypes.CREATE_INVOICE,
        variables: {
          clientId,
          userId: user.id,
          companyId,
          createInvoiceTaxesInput,
          createInvoiceInput: {
            ...createInvoiceInput,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null
          },
        },
      })
        .then((result) => {
          const invoice = result.data?.createInvoice!;
          resolve(invoice);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const createDeposit =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Deposit>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getCurrentUser, getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const user = getCurrentUser();
      const depositCondition = getRuleCondition('create', 'Deposit');
      const clientId = depositCondition?.clientId?.in?.[0] ?? 0;
      const {
        company: { id: companyId },
        group,
        ...newDepositForm
      } = getInvoice('forms.deposits.create');
      const selectedGroupId = getInvoice("selectedGroupId");

      MUTATE<CreateDepositMutation>({
        mutation: queryTypes.CREATE_DEPOSIT,
        variables: {
          clientId,
          userId: user.id,
          createDepositInput: {
            companyId,
            ...newDepositForm,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const deposit = result.data?.createDeposit!;
          resolve(deposit);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const createLogistic =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Logistic>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getCurrentUser, getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const user = getCurrentUser();
      const logisticCondition = getRuleCondition('create', 'Logistic');
      const clientId = logisticCondition?.clientId?.in?.[0] ?? 0;

      const {
        company: { id: companyId },
        nrcInvoiceNumber,
        clientInvoiceNumber,
        crt,
        clientName,
        quantityVolume,
        warehouseDispatchDate,
        revenueDate,
        customsDate,
        storageDate,
        clientDate,
        releaseDate,
        observation,
        group,
      } = getInvoice('forms.logistics.create');
      const selectedGroupId = getInvoice("selectedGroupId");
      MUTATE<CreateLogisticMutation>({
        mutation: queryTypes.CREATE_LOGISTIC,
        variables: {
          clientId,
          userId: user.id,
          createLogisticInput: {
            companyId,
            nrcInvoiceNumber: nrcInvoiceNumber || null,
            clientInvoiceNumber: clientInvoiceNumber || null,
            crt: crt || null,
            clientName: clientName || null,
            quantityVolume: quantityVolume || null,
            warehouseDispatchDate: warehouseDispatchDate || null,
            revenueDate: revenueDate || null,
            customsDate: customsDate || null,
            storageDate: storageDate || null,
            clientDate: clientDate || null,
            releaseDate: releaseDate || null,
            observation: observation || null,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const logistic = result.data?.createLogistic!;
          resolve(logistic);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const createLogisticOrigin =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<LogisticOrigin>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getCurrentUser, getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const user = getCurrentUser();
      const logisticOriginCondition = getRuleCondition('create', 'Logistic');
      const clientId = logisticOriginCondition?.clientId?.in?.[0] ?? 0;

      const {
        company: { id: companyId },
        originInvoiceNumber,
        quantityVolume,
        releaseDate,
        group,
      } = getInvoice('forms.logisticOrigins.create');
      const selectedGroupId = getInvoice("selectedGroupId");
      MUTATE<CreateLogisticOriginMutation>({
        mutation: queryTypes.CREATE_LOGISTIC_ORIGIN,
        variables: {
          clientId,
          userId: user.id,
          createLogisticOriginInput: {
            companyId,
            originInvoiceNumber: originInvoiceNumber || null,
            quantityVolume: quantityVolume || null,
            releaseDate: releaseDate || null,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const logisticOrigin = result.data?.createLogisticOrigin!;
          resolve(logisticOrigin);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const createDeposits =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
        user: { getCurrentUser, getRuleCondition },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const user = getCurrentUser();

      const depositCondition = getRuleCondition('create', 'Deposit');
      const clientId = depositCondition?.clientId?.in?.[0] ?? 0;
      const selectedGroupId = getInvoice("selectedGroupId");

      const createDepositsInput = getInvoice('forms.deposits.createMany').map(
        ({ company: { id: companyId }, group, ...form }) => ({
          ...form,
          companyId,
          groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,

        }),
      );

      MUTATE<CreateDepositsMutation>({
        mutation: queryTypes.CREATE_DEPOSITS,
        variables: {
          clientId,
          userId: user.id,
          createDepositsInput,
        },
      })
        .then((result) => {
          const deposit = result.data?.createDeposits!;
          resolve(deposit);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateLockInvoice =
  (locked: boolean) => (dispatch: DispatchType, getState: () => any) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      const invoiceIds = getInvoice('selecteds.invoices');
      MUTATE<UpdateLockInvoiceMutation>({
        mutation: queryTypes.UPDATE_LOCK_INVOICE,
        variables: {
          invoiceIds,
          locked,
        },
      })
        .then((result) => {
          resolve(result.data?.updateLockInvoice!);
        })
        .catch(reject);
    });

export const filterInvoiceQuery =
  (searchFilter: SearchFilter) =>
    (dispatch: DispatchType, getState: () => any) =>
      new Promise<ApolloQueryResult<FilterInvoiceQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);
        const selectedGroupId = getInvoice("selectedGroupId");
        QUERY<FilterInvoiceQuery>({
          query: queryTypes.FILTER_INVOICE,
          variables: {
            searchFilter: {
              ...searchFilter,
              filter: [
                ...(searchFilter.filter ?? []),
                ...(selectedGroupId ? [{
                  column: 'groupId',
                  value: selectedGroupId
                }] : []),
              ]
            }
          },
        })
          .then(resolve)
          .catch(reject);
      });

export const invoiceDashboardQuery = () => (dispatch: DispatchType, getState: () => any) =>
  new Promise<ApolloQueryResult<InvoiceDashboardQuery>>((resolve, reject) => {
    const {
      invoice: { getInvoice },
    } = rootGetters(getState());
    const {
      graphql: { QUERY },
    } = rootSetters(dispatch);
    const selectedGroupId = getInvoice("selectedGroupId");
    QUERY<InvoiceDashboardQuery>({
      query: queryTypes.INVOICE_DASHBOARD,
      variables: { groupId: selectedGroupId },
    })
      .then(resolve)
      .catch(reject);
  });

export const depositDashboardQuery = () => (dispatch: DispatchType, getState: () => any) =>
  new Promise<ApolloQueryResult<DepositDashboardQuery>>((resolve, reject) => {
    const {
      invoice: { getInvoice },
    } = rootGetters(getState());
    const {
      graphql: { QUERY },
    } = rootSetters(dispatch);
    const selectedGroupId = getInvoice("selectedGroupId");
    QUERY<DepositDashboardQuery>({
      query: queryTypes.DEPOSIT_DASHBOARD,
      variables: { groupId: selectedGroupId },
    })
      .then(resolve)
      .catch(reject);
  });

export const invoiceDashboard = () => (dispatch: DispatchType) =>
  new Promise<ApolloQueryResult<InvoiceDashboardQuery>>((resolve, reject) => {
    const {
      invoice: { setInvoice, invoiceDashboardQuery },
    } = rootSetters(dispatch);
    invoiceDashboardQuery()
      .then((result) => {
        const { data } = result.data.invoiceDashboard;
        setInvoice('dashboard.invoice.data', data);
        resolve(result);
      })
      .catch(reject);
  });

export const depositDashboard = () => (dispatch: DispatchType) =>
  new Promise<ApolloQueryResult<DepositDashboardQuery>>((resolve, reject) => {
    const {
      invoice: { setInvoice, depositDashboardQuery },
    } = rootSetters(dispatch);
    depositDashboardQuery()
      .then((result) => {
        const { data } = result.data.depositDashboard;
        setInvoice('dashboard.deposit.data', data);
        resolve(result);
      })
      .catch(reject);
  });

export const filterInvoice =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterInvoiceQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { setInvoice, filterInvoiceQuery },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.invoices.filter');
      filterInvoiceQuery(searchFilter)
        .then((result) => {
          const { filterInvoice } = result.data;
          setInvoice('filters.invoices.data', filterInvoice);
          setInvoice(
            'loadings.deleteInvoices',
            filterInvoice.data.map(() => false),
          );
          setInvoice(
            'selecteds.invoices',
            getInvoice('selecteds.invoices').filter((id) =>
              filterInvoice.data.find((invoice) => id === invoice.id),
            ),
          );
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterCompleteInvoice =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterInvoiceQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { filterInvoiceQuery },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.invoices.filter');
      filterInvoiceQuery({
        ...searchFilter,
        page: 1,
        limit: -1,
      })
        .then(resolve)
        .catch(reject);
    });

export const filterInvoiceProduct =
  (searchQuery: string) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterInvoiceProductQuery>>(
      (resolve, reject) => {
        const {
          user: { getRuleCondition },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
          invoice: { setInvoice },
        } = rootSetters(dispatch);
        const invoiceCondition = getRuleCondition('read', 'Invoice');
        const clientId = invoiceCondition?.clientId?.in?.[0] ?? 0;
        QUERY<FilterInvoiceProductQuery>({
          query: queryTypes.FILTER_INVOICE_PRODUCT,
          variables: {
            clientId,
            searchQuery,
          },
        })
          .then((result) => {
            const { filterInvoiceProduct } = result.data;
            setInvoice('filters.invoiceProducts', filterInvoiceProduct);
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const filterDailyDepositQuery =
  (searchFilter: SearchFilter) =>
    (dispatch: DispatchType, getState: () => any) =>
      new Promise<ApolloQueryResult<FilterDailyDepositQuery>>(
        (resolve, reject) => {
          const {
            invoice: { getInvoice },
          } = rootGetters(getState());
          const {
            graphql: { QUERY },
          } = rootSetters(dispatch);
          const selectedGroupId = getInvoice("selectedGroupId");
          QUERY<FilterDailyDepositQuery>({
            query: queryTypes.FILTER_DAILY_DEPOSIT,
            variables: {
              searchFilter: {
                ...searchFilter,
                filter: [
                  ...(searchFilter.filter ?? []),
                  ...(selectedGroupId ? [{
                    column: 'groupId',
                    value: selectedGroupId
                  }] : []),
                ]
              }
            },
          })
            .then(resolve)
            .catch((error) => {
              reject(error);
            });
        },
      );

export const filterDailyDeposit =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterDailyDepositQuery>>(
      (resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          invoice: { setInvoice, filterDailyDepositQuery },
        } = rootSetters(dispatch);
        const { filter } = getInvoice('filters.deposits.filter');
        const { page, limit, orderBy } = getInvoice(
          'filters.dailyDeposits.filter',
        );
        const searchFilter = {
          filter,
          orderBy,
          page,
          limit,
        };
        filterDailyDepositQuery(searchFilter)
          .then((result) => {
            const { filterDailyDeposit } = result.data;
            setInvoice('filters.dailyDeposits.data', filterDailyDeposit);
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const filterDepositQuery =
  (searchFilter: SearchFilter) =>
    (dispatch: DispatchType, getState: () => any) =>
      new Promise<ApolloQueryResult<FilterDepositQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);
        const selectedGroupId = getInvoice("selectedGroupId");
        QUERY<FilterDepositQuery>({
          query: queryTypes.FILTER_DEPOSIT,
          variables: {
            searchFilter: {
              ...searchFilter,
              filter: [
                ...(searchFilter.filter ?? []),
                ...(selectedGroupId ? [{
                  column: 'groupId',
                  value: selectedGroupId
                }] : []),
              ]
            }
          },
        })
          .then(resolve)
          .catch(reject);
      });

export const filterLogisticQuery =
  (searchFilter: SearchFilter) =>
    (dispatch: DispatchType, getState: () => any) =>
      new Promise<ApolloQueryResult<FilterLogisticQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);
        const selectedGroupId = getInvoice("selectedGroupId");
        QUERY<FilterLogisticQuery>({
          query: queryTypes.FILTER_LOGISTIC,
          variables: {
            searchFilter: {
              ...searchFilter,
              filter: [
                ...(searchFilter.filter ?? []),
                ...(selectedGroupId ? [{
                  column: 'groupId',
                  value: selectedGroupId
                }] : []),
              ]
            }
          },
        })
          .then(resolve)
          .catch(reject);
      });

export const filterLogistic =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterLogisticQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { setInvoice, filterLogisticQuery },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.logistics.filter');
      filterLogisticQuery(searchFilter)
        .then((result) => {
          const { filterLogistic } = result.data;
          setInvoice('filters.logistics.data', filterLogistic);
          setInvoice(
            'loadings.deleteLogistics',
            filterLogistic.data.map(() => false),
          );
          setInvoice(
            'selecteds.logistics',
            getInvoice('selecteds.logistics').filter((id) =>
              filterLogistic.data.find((deposit) => id === deposit.id),
            ),
          );
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterLogisticOriginQuery =
  (searchFilter: SearchFilter) =>
    (dispatch: DispatchType, getState: () => any) =>
      new Promise<ApolloQueryResult<FilterLogisticOriginQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);
        const selectedGroupId = getInvoice("selectedGroupId");
        QUERY<FilterLogisticOriginQuery>({
          query: queryTypes.FILTER_LOGISTIC_ORIGIN,
          variables: {
            searchFilter: {
              ...searchFilter,
              filter: [
                ...(searchFilter.filter ?? []),
                ...(selectedGroupId ? [{
                  column: 'groupId',
                  value: selectedGroupId
                }] : []),
              ]
            }
          },
        })
          .then(resolve)
          .catch(reject);
      });

export const filterLogisticOrigin =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterLogisticOriginQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { setInvoice, filterLogisticOriginQuery },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.logisticOrigins.filter');
      filterLogisticOriginQuery(searchFilter)
        .then((result) => {
          const { filterLogisticOrigin } = result.data;
          setInvoice('filters.logisticOrigins.data', filterLogisticOrigin);
          setInvoice(
            'loadings.deleteLogisticOrigins',
            filterLogisticOrigin.data.map(() => false),
          );
          setInvoice(
            'selecteds.logisticOrigins',
            getInvoice('selecteds.logisticOrigins').filter((id) =>
              filterLogisticOrigin.data.find((deposit) => id === deposit.id),
            ),
          );
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterDeposit =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterDepositQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { setInvoice, filterDailyDeposit, filterDepositQuery },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.deposits.filter');
      filterDepositQuery(searchFilter)
        .then((result) => {
          const { filterDeposit } = result.data;
          setInvoice('filters.deposits.data', filterDeposit);
          setInvoice(
            'loadings.deleteDeposits',
            filterDeposit.data.map(() => false),
          );
          setInvoice(
            'selecteds.deposits',
            getInvoice('selecteds.deposits').filter((id) =>
              filterDeposit.data.find((deposit) => id === deposit.id),
            ),
          );
          filterDailyDeposit()
            .then(() => {
              resolve(result);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterCompleteDeposit =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<
      [
        ApolloQueryResult<FilterDepositQuery>,
        ApolloQueryResult<FilterDailyDepositQuery>,
      ]
    >((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { filterDailyDepositQuery, filterDepositQuery },
      } = rootSetters(dispatch);
      const searchFilter = {
        ...getInvoice('filters.deposits.filter'),
        limit: -1,
      };
      Promise.all([
        filterDepositQuery(searchFilter),
        filterDailyDepositQuery(searchFilter),
      ])
        .then(resolve)
        .then(reject);
    });

export const filterTax =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterTaxQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { QUERY },
        invoice: { setInvoice },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.taxes.filter');
      QUERY<FilterTaxQuery>({
        query: queryTypes.FILTER_TAX,
        variables: {
          searchFilter,
        },
      })
        .then((result) => {
          const { filterTax } = result.data;
          setInvoice('filters.taxes.data', filterTax);
          setInvoice(
            'loadings.deleteTaxes',
            filterTax.data.map(() => false),
          );
          setInvoice(
            'selecteds.taxes',
            getInvoice('selecteds.taxes').filter((id) =>
              filterTax.data.find((tax) => id === tax.id),
            ),
          );
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterCompany =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterCompanyQuery>>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { QUERY },
        invoice: { setInvoice },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.companies.filter');
      QUERY<FilterCompanyQuery>({
        query: queryTypes.FILTER_COMPANY,
        variables: {
          searchFilter,
        },
      })
        .then((result) => {
          const { filterCompany } = result.data;
          setInvoice('filters.companies.data', filterCompany);
          setInvoice(
            'loadings.deleteCompanies',
            filterCompany.data.map(() => false),
          );
          setInvoice(
            'selecteds.companies',
            getInvoice('selecteds.companies').filter((id) =>
              filterCompany.data.find((company) => id === company.id),
            ),
          );
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterSelectCompanies =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterCompanyQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { QUERY },
        invoice: { setInvoice },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.selectCompanies.filter');
      const companyCondition = getRuleCondition('read', 'Company');
      const companyIds = companyCondition?.id?.in ?? [];
      const clientId = companyCondition?.clientId?.in?.[0] ?? 0;
      QUERY<FilterCompanyQuery>({
        query: queryTypes.FILTER_COMPANY,
        variables: {
          clientId,
          companyIds,
          searchFilter: {
            ...searchFilter,
            limit: 25,
          },
        },
      })
        .then((result) => {
          const { filterCompany } = result.data;
          setInvoice('filters.selectCompanies.data', filterCompany);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterSelectTaxes =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterTaxQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { QUERY },
        invoice: { setInvoice },
      } = rootSetters(dispatch);
      const searchFilter = getInvoice('filters.selectTaxes.filter');
      const taxCondition = getRuleCondition('read', 'Tax');
      const clientId = taxCondition?.clientId?.in?.[0] ?? 0;
      QUERY<FilterTaxQuery>({
        query: queryTypes.FILTER_TAX,
        variables: {
          clientId,
          searchFilter,
        },
      })
        .then((result) => {
          const { filterTax } = result.data;
          setInvoice('filters.selectTaxes.data', filterTax);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const autocompleteInvoice =
  (field: keyof InvoiceState['autocompletes']['invoices']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteInvoiceQuery>>(
        (resolve, reject) => {
          const {
            invoice: { getInvoice },
          } = rootGetters(getState());
          const {
            graphql: { QUERY },
            invoice: { setInvoice },
          } = rootSetters(dispatch);
          const autocompleteFilter = getInvoice(
            `autocompletes.invoices.${field}.filter`,
          );
          QUERY<AutocompleteInvoiceQuery>({
            query: queryTypes.AUTOCOMPLETE_INVOICE,
            variables: {
              autocompleteFilter,
            },
          })
            .then((result) => {
              const { autocompleteInvoice } = result.data;
              setInvoice(
                `autocompletes.invoices.${field}.data`,
                autocompleteInvoice,
              );
              resolve(result);
            })
            .catch((error) => {
              reject(error);
            });
        },
      );

export const autocompleteDeposit =
  (field: keyof InvoiceState['autocompletes']['deposits']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteDepositQuery>>(
        (resolve, reject) => {
          const {
            invoice: { getInvoice },
          } = rootGetters(getState());
          const {
            graphql: { QUERY },
            invoice: { setInvoice },
          } = rootSetters(dispatch);
          const autocompleteFilter = getInvoice(
            `autocompletes.deposits.${field}.filter`,
          );
          QUERY<AutocompleteDepositQuery>({
            query: queryTypes.AUTOCOMPLETE_DEPOSIT,
            variables: {
              autocompleteFilter,
            },
          })
            .then((result) => {
              const { autocompleteDeposit } = result.data;
              setInvoice(
                `autocompletes.deposits.${field}.data`,
                autocompleteDeposit,
              );

              resolve(result);
            })
            .catch((error) => {
              reject(error);
            });
        },
      );

export const autocompleteTax =
  (field: keyof InvoiceState['autocompletes']['taxes']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteTaxQuery>>((resolve, reject) => {
        const {
          user: { getRuleCondition },
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
          invoice: { setInvoice },
        } = rootSetters(dispatch);
        const autocompleteFilter = getInvoice(
          `autocompletes.taxes.${field}.filter`,
        );
        const taxCondition = getRuleCondition('read', 'Tax');
        const clientId = taxCondition?.clientId?.in?.[0] ?? 0;
        QUERY<AutocompleteTaxQuery>({
          query: queryTypes.AUTOCOMPLETE_TAX,
          variables: {
            clientId,
            autocompleteFilter,
          },
        })
          .then((result) => {
            const { autocompleteTax } = result.data;
            setInvoice(`autocompletes.taxes.${field}.data`, autocompleteTax);

            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      });

export const autocompleteLogistic =
  (field: keyof InvoiceState['autocompletes']['logistics']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteLogisticQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
          invoice: { setInvoice },
        } = rootSetters(dispatch);
        const autocompleteFilter = getInvoice(
          `autocompletes.logistics.${field}.filter`,
        );
        QUERY<AutocompleteLogisticQuery>({
          query: queryTypes.AUTOCOMPLETE_LOGISTIC,
          variables: {
            autocompleteFilter,
          },
        })
          .then((result) => {
            const { autocompleteLogistic } = result.data;
            setInvoice(`autocompletes.logistics.${field}.data`, autocompleteLogistic);
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      });

export const autocompleteLogisticOrigin =
  (field: keyof InvoiceState['autocompletes']['logisticOrigins']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteLogisticOriginQuery>>((resolve, reject) => {
        const {
          invoice: { getInvoice },
        } = rootGetters(getState());
        const {
          graphql: { QUERY },
          invoice: { setInvoice },
        } = rootSetters(dispatch);
        const autocompleteFilter = getInvoice(
          `autocompletes.logisticOrigins.${field}.filter`,
        );
        QUERY<AutocompleteLogisticOriginQuery>({
          query: queryTypes.AUTOCOMPLETE_LOGISTIC_ORIGIN,
          variables: {
            autocompleteFilter,
          },
        })
          .then((result) => {
            const { autocompleteLogisticOrigin } = result.data;
            setInvoice(`autocompletes.logisticOrigins.${field}.data`, autocompleteLogisticOrigin);

            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      });

export const autocompleteCompany =
  (field: keyof InvoiceState['autocompletes']['companies']) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<ApolloQueryResult<AutocompleteCompanyQuery>>(
        (resolve, reject) => {
          const {
            user: { getRuleCondition },
            invoice: { getInvoice },
          } = rootGetters(getState());
          const {
            graphql: { QUERY },
            invoice: { setInvoice },
          } = rootSetters(dispatch);
          const autocompleteFilter = getInvoice(
            `autocompletes.companies.${field}.filter`,
          );
          const companyCondition = getRuleCondition('read', 'Company');
          const companyIds = companyCondition?.id?.in ?? [];
          const clientId = companyCondition?.clientId?.in?.[0] ?? 0;
          QUERY<AutocompleteCompanyQuery>({
            query: queryTypes.AUTOCOMPLETE_COMPANY,
            variables: {
              clientId,
              companyIds,
              autocompleteFilter,
            },
          })
            .then((result) => {
              const { autocompleteCompany } = result.data;
              setInvoice(
                `autocompletes.companies.${field}.data`,
                autocompleteCompany,
              );

              resolve(result);
            })
            .catch((error) => {
              reject(error);
            });
        },
      );

export const deleteInvoices =
  (invoiceIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteInvoicesMutation>({
        mutation: queryTypes.DELETE_INVOICES,
        variables: {
          invoiceIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteInvoices!;
          const invoices = getInvoice('filters.invoices.data.data');
          setInvoice(
            'filters.invoices.data.data',
            invoices.filter(({ id }) => !invoiceIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const deleteTaxes =
  (taxIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteTaxesMutation>({
        mutation: queryTypes.DELETE_TAXES,
        variables: {
          taxIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteTaxes!;
          const taxes = getInvoice('filters.taxes.data.data');
          setInvoice(
            'filters.taxes.data.data',
            taxes.filter(({ id }) => !taxIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const deleteCompanies =
  (companyIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteCompaniesMutation>({
        mutation: queryTypes.DELETE_COMPANIES,
        variables: {
          companyIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteCompanies!;
          const companies = getInvoice('filters.companies.data.data');
          setInvoice(
            'filters.companies.data.data',
            companies.filter(({ id }) => !companyIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const deleteDeposits =
  (depositIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteDepositsMutation>({
        mutation: queryTypes.DELETE_DEPOSITS,
        variables: {
          depositIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteDeposits!;
          const deposits = getInvoice('filters.deposits.data.data');
          setInvoice(
            'filters.deposits.data.data',
            deposits.filter(({ id }) => !depositIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const deleteLogistics =
  (logisticIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteLogisticsMutation>({
        mutation: queryTypes.DELETE_LOGISTICS,
        variables: {
          logisticIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteLogistics!;
          const logistics = getInvoice('filters.logistics.data.data');
          setInvoice(
            'filters.logistics.data.data',
            logistics.filter(({ id }) => !logisticIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const deleteLogisticOrigins =
  (logisticOriginIds: number[]) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      MUTATE<DeleteLogisticOriginsMutation>({
        mutation: queryTypes.DELETE_LOGISTIC_ORIGINS,
        variables: {
          logisticOriginIds,
        },
      })
        .then((result) => {
          const data = result.data?.deleteLogisticOrigins!;
          const logisticOrigins = getInvoice('filters.logisticOrigins.data.data');
          setInvoice(
            'filters.logisticOrigins.data.data',
            logisticOrigins.filter(({ id }) => !logisticOriginIds.includes(id)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateInvoice =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<Invoice>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);
      const {
        id: invoiceId,
        company: { id: companyId },
        invoiceTaxes,
        group,
        ...updateInvoiceInput
      } = getInvoice('forms.invoices.update');
      const selectedGroupId = getInvoice("selectedGroupId");
      const createInvoiceTaxesInput = invoiceTaxes.map(
        ({ value, tax: { id } }) => ({ taxId: id, value }),
      );
      MUTATE<UpdateInvoicesMutation>({
        mutation: queryTypes.UPDATE_INVOICE,
        variables: {
          invoiceId,
          companyId,
          createInvoiceTaxesInput,
          updateInvoiceInput: {
            ...updateInvoiceInput,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const data = result.data?.updateInvoice!;
          const invoices = getInvoice('filters.invoices.data.data');
          setInvoice(
            'filters.invoices.data.data',
            invoices.map((invoice) =>
              invoice.id === invoiceId ? data : invoice,
            ),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateTax =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<Tax>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      const { id: taxId, ...updateTaxInput } = getInvoice('forms.taxes.update');

      MUTATE<UpdateTaxesMutation>({
        mutation: queryTypes.UPDATE_TAX,
        variables: {
          taxId,
          updateTaxInput,
        },
      })
        .then((result) => {
          const data = result.data?.updateTax!;
          const taxes = getInvoice('filters.taxes.data.data');
          setInvoice(
            'filters.taxes.data.data',
            taxes.map((tax) => (tax.id === taxId ? data : tax)),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateCompany =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<Company>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      const { id: companyId, ...updateCompanyInput } = getInvoice(
        'forms.companies.update',
      );

      MUTATE<UpdateCompaniesMutation>({
        mutation: queryTypes.UPDATE_COMPANY,
        variables: {
          companyId,
          updateCompanyInput,
        },
      })
        .then((result) => {
          const data = result.data?.updateCompany!;
          const companies = getInvoice('filters.companies.data.data');
          setInvoice(
            'filters.companies.data.data',
            companies.map((company) =>
              company.id === companyId ? data : company,
            ),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateManyDeposits =
  (depositIds: number[], updateDepositInput: UpdateDepositInput) =>
    (dispatch: DispatchType) =>
      new Promise<BatchResponse>((resolve, reject) => {
        const {
          graphql: { MUTATE },
        } = rootSetters(dispatch);
        MUTATE<UpdateManyDepositsMutation>({
          mutation: queryTypes.UPDATE_MANY_DEPOSITS,
          variables: {
            depositIds,
            updateDepositInput,
          },
        })
          .then((result) => {
            const data = result.data?.updateManyDeposits!;
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      });

export const updateDeposits =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { updateManyDeposits },
      } = rootSetters(dispatch);
      const depositIds = getInvoice('selecteds.deposits');
      const depositForm = getInvoice('forms.deposits.updateMany');
      updateManyDeposits(depositIds, depositForm)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateLockDeposit =
  (locked: boolean) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        invoice: { updateManyDeposits },
      } = rootSetters(dispatch);
      const depositIds = getInvoice('selecteds.deposits');
      updateManyDeposits(depositIds, { locked })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateDeposit =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<Deposit>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      const {
        id: depositId,
        company: { id: companyId },
        group,
        ...updateDepositForm
      } = getInvoice('forms.deposits.update');
      const selectedGroupId = getInvoice("selectedGroupId");

      MUTATE<UpdateDepositMutation>({
        mutation: queryTypes.UPDATE_DEPOSIT,
        variables: {
          depositId,
          updateDepositInput: {
            ...updateDepositForm,
            companyId,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const data = result.data?.updateDeposit!;
          const deposits = getInvoice('filters.deposits.data.data');
          setInvoice(
            'filters.deposits.data.data',
            deposits.map((deposit) =>
              deposit.id === depositId ? data : deposit,
            ),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateLogistic =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<Logistic>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      const {
        id: logisticId,
        company: { id: companyId },
        clientInvoiceNumber,
        nrcInvoiceNumber,
        crt,
        clientName,
        quantityVolume,
        warehouseDispatchDate,
        revenueDate,
        customsDate,
        storageDate,
        clientDate,
        releaseDate,
        observation,
        group,
      } = getInvoice('forms.logistics.update');
      const selectedGroupId = getInvoice("selectedGroupId");
      MUTATE<UpdateLogisticMutation>({
        mutation: queryTypes.UPDATE_LOGISTIC,
        variables: {
          logisticId,
          updateLogisticInput: {
            companyId,
            clientInvoiceNumber: clientInvoiceNumber || null,
            nrcInvoiceNumber: nrcInvoiceNumber || null,
            crt: crt || null,
            clientName: clientName || null,
            quantityVolume: quantityVolume || null,
            warehouseDispatchDate: warehouseDispatchDate || null,
            revenueDate: revenueDate || null,
            customsDate: customsDate || null,
            storageDate: storageDate || null,
            clientDate: clientDate || null,
            releaseDate: releaseDate || null,
            observation: observation || null,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const data = result.data?.updateLogistic!;
          const logistics = getInvoice('filters.logistics.data.data');
          setInvoice(
            'filters.logistics.data.data',
            logistics.map((logistic) =>
              logistic.id === logisticId ? data : logistic,
            ),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateLogisticOrigin =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<LogisticOrigin>((resolve, reject) => {
      const {
        invoice: { getInvoice },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
        invoice: { setInvoice },
      } = rootSetters(dispatch);

      const {
        id: logisticOriginId,
        company: { id: companyId },
        originInvoiceNumber,
        quantityVolume,
        releaseDate,
        group,
      } = getInvoice('forms.logisticOrigins.update');
      const selectedGroupId = getInvoice("selectedGroupId");
      MUTATE<UpdateLogisticOriginMutation>({
        mutation: queryTypes.UPDATE_LOGISTIC_ORIGIN,
        variables: {
          logisticOriginId,
          updateLogisticOriginInput: {
            companyId,
            originInvoiceNumber: originInvoiceNumber || null,
            quantityVolume: quantityVolume || null,
            releaseDate: releaseDate || null,
            groupId: selectedGroupId ? selectedGroupId : group.id ? group.id : null,
          },
        },
      })
        .then((result) => {
          const data = result.data?.updateLogisticOrigin!;
          const logisticOrigins = getInvoice('filters.logisticOrigins.data.data');
          setInvoice(
            'filters.logisticOrigins.data.data',
            logisticOrigins.map((logisticOrigin) =>
              logisticOrigin.id === logisticOriginId ? data : logisticOrigin,
            ),
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const validateDepositsForm =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<boolean>((resolve, reject) => {
      rootSetters(dispatch).invoice.setInvoice(
        `blur.deposits.createMany`,
        getState().invoice.blur.deposits.createMany.map(() =>
          defaultBlurDepositForm(true),
        ),
      );

      const messages = rootGetters(getState()).invoice.getDepositsFormMessage();
      const isValid = !messages.some((form) =>
        Object.values(form).some((message) => message !== ''),
      );

      if (isValid) {
        resolve(true);
      } else {
        reject(false);
      }
    });

export const validateForm =
  (form: FlattenKeys<InvoiceState['blur'], 2, 1>) =>
    (dispatch: DispatchType, getState: GetStateType) =>
      new Promise<boolean>((resolve, reject) => {
        let isValid = false;

        if (form === 'invoices.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.invoices.create`,
            defaultBlurInvoiceForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('invoices.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'invoices.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.invoices.update`,
            defaultBlurInvoiceForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('invoices.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'taxes.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.taxes.create`,
            defaultBlurTaxForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('taxes.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'taxes.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.taxes.update`,
            defaultBlurTaxForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('taxes.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'companies.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.companies.create`,
            defaultBlurCompanyForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('companies.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'companies.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.companies.update`,
            defaultBlurCompanyForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('companies.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'deposits.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.deposits.create`,
            defaultBlurDepositForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('deposits.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'deposits.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.deposits.update`,
            defaultBlurDepositForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('deposits.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'deposits.updateMany') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.deposits.updateMany`,
            defaultBlurManyDeposits(true),
          );
          const messages = rootGetters(getState()).invoice.getFormMessage(
            'deposits.updateMany',
          );
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'logistics.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.logistics.create`,
            defaultBlurLogisticForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('logistics.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'logistics.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.logistics.update`,
            defaultBlurLogisticForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('logistics.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'logisticOrigins.create') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.logisticOrigins.create`,
            defaultBlurLogisticOriginForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('logisticOrigins.create');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (form === 'logisticOrigins.update') {
          rootSetters(dispatch).invoice.setInvoice(
            `blur.logisticOrigins.update`,
            defaultBlurLogisticOriginForm(true),
          );
          const messages =
            rootGetters(getState()).invoice.getFormMessage('logisticOrigins.update');
          isValid = !Object.values(messages).some((message) => message !== '');
        }

        if (isValid) {
          resolve(true);
        } else {
          reject(false);
        }
      });

export const filterGroupQuery =
  (clientIds?: number[], groupIds?: number[]) =>
    (dispatch: DispatchType) =>
      new Promise<ApolloQueryResult<FilterGroupQuery>>((resolve, reject) => {
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);
        QUERY<FilterGroupQuery>({
          query: queryTypes.FILTER_GROUP,
          variables: {
            clientIds,
            groupIds,
          },
        })
          .then(resolve)
          .catch(reject);
      });


export const filterGroupInvoice =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterGroupQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        invoice: { filterGroupQuery, setInvoice },
      } = rootSetters(dispatch);
      const invoiceCondition = getRuleCondition('read', 'Invoice');
      const clientIds = invoiceCondition?.clientId?.in;
      const groupIds = invoiceCondition?.groupId?.in;

      filterGroupQuery(clientIds, groupIds)
        .then((result) => {
          setInvoice("groups.invoice", result.data.filterGroup);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterGroupDeposit =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterGroupQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        invoice: { filterGroupQuery, setInvoice },
      } = rootSetters(dispatch);
      const depositCondition = getRuleCondition('read', 'Deposit');
      const clientIds = depositCondition?.clientId?.in;
      const groupIds = depositCondition?.groupId?.in;

      filterGroupQuery(clientIds, groupIds)
        .then((result) => {
          setInvoice("groups.deposit", result.data.filterGroup);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterGroupLogistic =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterGroupQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        invoice: { filterGroupQuery, setInvoice },
      } = rootSetters(dispatch);
      const logisticCondition = getRuleCondition('read', 'Logistic');
      const clientIds = logisticCondition?.clientId?.in;
      const groupIds = logisticCondition?.groupId?.in;

      filterGroupQuery(clientIds, groupIds)
        .then((result) => {
          setInvoice("groups.logistic", result.data.filterGroup);
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterUserGroups =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterGroupQuery>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
        invoice: { getInvoice }
      } = rootGetters(getState());
      const {
        invoice: { filterGroupQuery, setInvoice },
      } = rootSetters(dispatch);

      const invoiceCondition = getRuleCondition('read', 'Invoice');
      const invoiceClientIds = invoiceCondition?.clientId?.in ?? [];
      const invoiceGroupIds = invoiceCondition?.groupId?.in ?? [];

      const depositCondition = getRuleCondition('read', 'Deposit');
      const depositClientIds = depositCondition?.clientId?.in ?? [];
      const depositGroupIds = depositCondition?.groupId?.in ?? [];

      const logisticCondition = getRuleCondition('read', 'Logistic');
      const logisticClientIds = logisticCondition?.clientId?.in ?? [];
      const logisticGroupIds = logisticCondition?.groupId?.in ?? [];

      const clientIds = [...invoiceClientIds, ...depositClientIds, ...logisticClientIds];
      const groupIds = [...invoiceGroupIds, ...depositGroupIds, ...logisticGroupIds];

      const selectedGroupId = getInvoice("selectedGroupId");
      filterGroupQuery(clientIds, groupIds)
        .then((result) => {
          const groups = result.data.filterGroup
          setInvoice("groups.all", groups);
          if (groups.length === 0) {
            setInvoice("selectedGroupId", null);
          } else {
            if (!groups.some(({ id }) => id === selectedGroupId)) {
              setInvoice("selectedGroupId", groups[0].id);
            }
          }
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const selectGroup =
  (groupId: number) => (dispatch: DispatchType) =>
    new Promise<void>(async (resolve, reject) => {
      const {
        invoice: {
          setInvoice,
          filterInvoice,
          filterDeposit,
          filterDailyDeposit,
          filterLogistic,
          filterLogisticOrigin,
          depositDashboard,
          invoiceDashboard,
        },
      } = rootSetters(dispatch);

      try {
        setInvoice("selectedGroupId", groupId ? groupId : null);
        setInvoice("filters.invoices.filter.page", 1);
        setInvoice("filters.deposits.filter.page", 1);
        setInvoice("filters.dailyDeposits.filter.page", 1);
        setInvoice("filters.logistics.filter.page", 1);
        setInvoice("filters.logisticOrigins.filter.page", 1);
        await Promise.all([
          filterInvoice(),
          filterDeposit(),
          filterDailyDeposit(),
          filterLogistic(),
          filterLogisticOrigin(),
          depositDashboard(),
          invoiceDashboard(),
        ])
        resolve();
      } catch (e) {
        reject();
      }

    });


const invoiceActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      selectGroup: (groupId: number) =>
        dispatch<Promise<void>>(
          selectGroup(groupId),
        ),
      filterInvoiceQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterInvoiceQuery>>>(
          filterInvoiceQuery(searchFilter),
        ),
      filterInvoice: () =>
        dispatch<Promise<ApolloQueryResult<FilterInvoiceQuery>>>(
          filterInvoice(),
        ),
      filterCompleteInvoice: () =>
        dispatch<Promise<ApolloQueryResult<FilterInvoiceQuery>>>(
          filterCompleteInvoice(),
        ),
      filterTax: () =>
        dispatch<Promise<ApolloQueryResult<FilterTaxQuery>>>(filterTax()),
      filterCompany: () =>
        dispatch<Promise<ApolloQueryResult<FilterCompanyQuery>>>(
          filterCompany(),
        ),
      filterSelectCompanies: () =>
        dispatch<Promise<ApolloQueryResult<FilterCompanyQuery>>>(
          filterSelectCompanies(),
        ),
      filterSelectTaxes: () =>
        dispatch<Promise<ApolloQueryResult<FilterTaxQuery>>>(
          filterSelectTaxes(),
        ),
      filterDailyDeposit: () =>
        dispatch<Promise<ApolloQueryResult<FilterDailyDepositQuery>>>(
          filterDailyDeposit(),
        ),
      filterDeposit: () =>
        dispatch<Promise<ApolloQueryResult<FilterDepositQuery>>>(
          filterDeposit(),
        ),
      filterLogistic: () =>
        dispatch<Promise<ApolloQueryResult<FilterLogisticQuery>>>(
          filterLogistic(),
        ),
      filterLogisticOrigin: () =>
        dispatch<Promise<ApolloQueryResult<FilterLogisticOriginQuery>>>(
          filterLogisticOrigin(),
        ),
      filterDepositQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterDepositQuery>>>(
          filterDepositQuery(searchFilter),
        ),
      filterLogisticQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterLogisticQuery>>>(
          filterLogisticQuery(searchFilter),
        ),
      filterLogisticOriginQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterLogisticOriginQuery>>>(
          filterLogisticOriginQuery(searchFilter),
        ),
      filterDailyDepositQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterDailyDepositQuery>>>(
          filterDailyDepositQuery(searchFilter),
        ),
      createInvoice: () => dispatch<Promise<Invoice>>(createInvoice()),
      createTax: () => dispatch<Promise<Tax>>(createTax()),
      createCompany: () => dispatch<Promise<Company>>(createCompany()),
      createDeposit: () => dispatch<Promise<Deposit>>(createDeposit()),
      createLogistic: () => dispatch<Promise<Logistic>>(createLogistic()),
      createLogisticOrigin: () => dispatch<Promise<LogisticOrigin>>(createLogisticOrigin()),
      createDeposits: () => dispatch<Promise<BatchResponse>>(createDeposits()),
      autocompleteInvoice: (
        field: keyof InvoiceState['autocompletes']['invoices'],
      ) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteInvoiceQuery>>>(
          autocompleteInvoice(field),
        ),
      autocompleteTax: (field: keyof InvoiceState['autocompletes']['taxes']) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteTaxQuery>>>(
          autocompleteTax(field),
        ),
      autocompleteLogistic: (field: keyof InvoiceState['autocompletes']['logistics']) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteLogisticQuery>>>(
          autocompleteLogistic(field),
        ),
      autocompleteLogisticOrigin: (field: keyof InvoiceState['autocompletes']['logisticOrigins']) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteLogisticOriginQuery>>>(
          autocompleteLogisticOrigin(field),
        ),
      autocompleteCompany: (
        field: keyof InvoiceState['autocompletes']['companies'],
      ) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteCompanyQuery>>>(
          autocompleteCompany(field),
        ),
      autocompleteDeposit: (
        field: keyof InvoiceState['autocompletes']['deposits'],
      ) =>
        dispatch<Promise<ApolloQueryResult<AutocompleteDepositQuery>>>(
          autocompleteDeposit(field),
        ),
      deleteInvoices: (invoiceIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteInvoices(invoiceIds)),
      deleteTaxes: (taxIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteTaxes(taxIds)),
      deleteCompanies: (companyIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteCompanies(companyIds)),
      deleteDeposits: (depositIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteDeposits(depositIds)),
      deleteLogistics: (logisticIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteLogistics(logisticIds)),
      deleteLogisticOrigins: (logisticOriginIds: number[]) =>
        dispatch<Promise<BatchResponse>>(deleteLogisticOrigins(logisticOriginIds)),
      updateCompany: () => dispatch<Promise<Company>>(updateCompany()),
      updateTax: () => dispatch<Promise<Tax>>(updateTax()),
      updateInvoice: () => dispatch<Promise<Invoice>>(updateInvoice()),
      updateDeposit: () => dispatch<Promise<Deposit>>(updateDeposit()),
      updateLogistic: () => dispatch<Promise<Logistic>>(updateLogistic()),
      updateLogisticOrigin: () => dispatch<Promise<LogisticOrigin>>(updateLogisticOrigin()),
      validateForm: (form: FlattenKeys<InvoiceState['blur'], 2, 1>) =>
        dispatch<Promise<boolean>>(validateForm(form)),
      validateDepositsForm: () =>
        dispatch<Promise<boolean>>(validateDepositsForm()),
      filterInvoiceProduct: (searchQuery: string) =>
        dispatch<Promise<ApolloQueryResult<FilterInvoiceProductQuery>>>(
          filterInvoiceProduct(searchQuery),
        ),
      filterCompleteDeposit: () =>
        dispatch<
          Promise<
            [
              ApolloQueryResult<FilterDepositQuery>,
              ApolloQueryResult<FilterDailyDepositQuery>,
            ]
          >
        >(filterCompleteDeposit()),
      updateLockInvoice: (locked: boolean) =>
        dispatch<Promise<BatchResponse>>(updateLockInvoice(locked)),
      updateManyDeposits: (
        depositIds: number[],
        updateDepositInput: UpdateDepositInput,
      ) =>
        dispatch<Promise<BatchResponse>>(
          updateManyDeposits(depositIds, updateDepositInput),
        ),
      updateDeposits: () => dispatch<Promise<BatchResponse>>(updateDeposits()),
      updateLockDeposit: (locked: boolean) =>
        dispatch<Promise<BatchResponse>>(updateLockDeposit(locked)),
      invoiceDashboardQuery: () =>
        dispatch<Promise<ApolloQueryResult<InvoiceDashboardQuery>>>(
          invoiceDashboardQuery(),
        ),
      depositDashboardQuery: () =>
        dispatch<Promise<ApolloQueryResult<DepositDashboardQuery>>>(
          depositDashboardQuery(),
        ),
      invoiceDashboard: () =>
        dispatch<Promise<ApolloQueryResult<InvoiceDashboardQuery>>>(
          invoiceDashboard(),
        ),
      depositDashboard: () =>
        dispatch<Promise<ApolloQueryResult<DepositDashboardQuery>>>(
          depositDashboard(),
        ),
      filterGroupQuery: (clientIds?: number[], groupIds?: number[]) =>
        dispatch<Promise<ApolloQueryResult<FilterGroupQuery>>>(
          filterGroupQuery(clientIds, groupIds),
        ),
      filterGroupInvoice: () =>
        dispatch<Promise<ApolloQueryResult<FilterGroupQuery>>>(
          filterGroupInvoice(),
        ),
      filterGroupDeposit: () =>
        dispatch<Promise<ApolloQueryResult<FilterGroupQuery>>>(
          filterGroupDeposit(),
        ),
      filterGroupLogistic: () =>
        dispatch<Promise<ApolloQueryResult<FilterGroupQuery>>>(
          filterGroupLogistic(),
        ),
      filterUserGroups: () =>
        dispatch<Promise<ApolloQueryResult<FilterGroupQuery>>>(
          filterUserGroups(),
        ),
    },
  );

export default invoiceActionCreators;
